import type { SupabaseClient } from "@supabase/supabase-js";

export interface FeedingPolicySlot {
  weekday: number;
  roundNo: number;
  startLocal: string;
  enabled: boolean;
}

export interface FeedingPolicyAdministrationSnapshot {
  canManage: boolean;
  policy?: {
    policyId: string;
    versionNo: number;
    policyName: string;
    timezoneName: string;
    activeWeekdays: number[];
    completionTargetMinutes: number;
    minimumGapMinutes: number;
    lateMode: string;
    cutoffMode: string;
    effectiveFrom?: string;
    slots: FeedingPolicySlot[];
    roundStarts: string[];
    scheduleUniform: boolean;
  };
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) return record(value[0]);
  if (value && typeof value === "object") return record(value);
  return undefined;
}

function numberList(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((item): item is number => typeof item === "number" && Number.isInteger(item))
    : [];
}

function slotList(value: unknown): FeedingPolicySlot[] {
  if (!Array.isArray(value)) return [];

  return value
    .map(record)
    .map((item) => ({
      weekday: Number(item.weekday),
      roundNo: Number(item.roundNo),
      startLocal: String(item.startLocal ?? ""),
      enabled: item.enabled === true,
    }))
    .filter(
      (item) =>
        Number.isInteger(item.weekday) &&
        item.weekday >= 0 &&
        item.weekday <= 6 &&
        Number.isInteger(item.roundNo) &&
        item.roundNo >= 1 &&
        /^([01]\d|2[0-3]):[0-5]\d$/.test(item.startLocal),
    );
}

function deriveRoundStarts(
  slots: FeedingPolicySlot[],
  activeWeekdays: number[],
): { roundStarts: string[]; scheduleUniform: boolean } {
  const schedules = activeWeekdays.map((weekday) =>
    slots
      .filter((slot) => slot.weekday === weekday && slot.enabled)
      .sort((a, b) => a.roundNo - b.roundNo)
      .map((slot) => slot.startLocal),
  );

  const first = schedules[0] ?? [];
  const serialized = JSON.stringify(first);
  return {
    roundStarts: first,
    scheduleUniform: schedules.every((schedule) => JSON.stringify(schedule) === serialized),
  };
}

export async function loadFeedingPolicyAdministration(
  client: SupabaseClient,
  farmId: string,
): Promise<FeedingPolicyAdministrationSnapshot> {
  const [permissionResult, policyResult] = await Promise.all([
    client.rpc("permission_decide", {
      target_farm_id: farmId,
      requested_permission: "settings.manage",
    }),
    client.rpc("feeding_policy_admin_current_v1", {
      target_farm_id: farmId,
    }),
  ]);

  if (permissionResult.error) throw permissionResult.error;
  if (policyResult.error) throw policyResult.error;

  const permission = firstRecord(permissionResult.data);
  const canManage = permission?.allowed === true;
  const row = firstRecord(policyResult.data);

  if (!row) return { canManage };

  const activeWeekdays = numberList(row.active_weekdays).sort((a, b) => a - b);
  const slots = slotList(row.slots);
  const derived = deriveRoundStarts(slots, activeWeekdays);

  return {
    canManage,
    policy: {
      policyId: String(row.policy_id),
      versionNo: Number(row.version_no),
      policyName: String(row.policy_name),
      timezoneName: String(row.timezone_name),
      activeWeekdays,
      completionTargetMinutes: Number(row.completion_target_minutes),
      minimumGapMinutes: Number(row.minimum_gap_minutes),
      lateMode: String(row.late_mode),
      cutoffMode: String(row.cutoff_mode),
      ...(typeof row.effective_from === "string" && row.effective_from
        ? { effectiveFrom: row.effective_from }
        : {}),
      slots,
      roundStarts: derived.roundStarts,
      scheduleUniform: derived.scheduleUniform,
    },
  };
}

export function farmLocalIsoDate(now: Date, timezoneName: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezoneName,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";

  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function jalaliFarmDate(isoDate: string, timezoneName: string): string {
  const date = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    timeZone: timezoneName,
    dateStyle: "long",
  }).format(date);
}
